import React from 'react'
import { Link } from 'react-router'

import CollapsibleList from '@/components/misc/CollapsibleList'
import SectionHeading from '@/components/misc/CompanyProfile/tabs/OverviewTab/SectionHeading'
import ExternalLink from '@/components/misc/ExternalLink'
import { Table, TableBody, TableCell, TableRow } from '@/components/ui/table'
import {
  COMPANY_PROFILE_IDENTIFIER_TYPE,
  COMPANY_TYPE_CODE,
} from '@/lib/constants'
import { bool2Human, extractURL } from '@/lib/helpers/helperFunctions'
import { currentUrl } from '@/lib/helpers/routeHelpers'
import { ROUTES } from '@/lib/routes'
import { CompanyProfileItem } from '@/types/responses/companies'

interface Props {
  companyProfile: CompanyProfileItem
}

interface Detail {
  label: string
  value: React.ReactNode | (() => React.ReactNode)
}

const AtAGlance: React.FC<Props> = ({ companyProfile }) => {
  const { directory, company } = companyProfile
  const isRegisteredAsIfa =
    companyProfile.company?.companyType.code === COMPANY_TYPE_CODE.ifa
  const websiteUrl = company?.url
    ? extractURL(company.url)
    : directory?.website
      ? extractURL(directory.website)
      : null
  const companyDetail = company?.companyDetail
  const companyNumber = company
    ? company.companyNumber
    : directory?.companyNumber
  const isFcaTradingName = !!directory?.fca?.frnTradingNameId
  const isFca = !!directory?.fca
  const isSra = !!directory?.sra

  const details: Detail[] = [
    {
      label: 'Company #',
      value: companyNumber ? (
        <ExternalLink
          href={`https://find-and-update.company-information.service.gov.uk/company/${companyNumber}`}
        >
          {companyNumber}
        </ExternalLink>
      ) : (
        'N/A'
      ),
    },
  ]

  if (websiteUrl) {
    details.push({
      label: 'Website',
      value: () => (
        <ExternalLink href={websiteUrl.href}>
          {websiteUrl.hostname}
        </ExternalLink>
      ),
    })
  }

  if (!isFcaTradingName && companyProfile.companiesHouse) {
    details.push({
      label: 'Years operational',
      value: (
        <>
          {Number(
            (new Date().getTime() -
              new Date(
                companyProfile.companiesHouse.dateIncorporated,
              ).getTime()) /
              31536000000,
          ).toFixed(0)}
        </>
      ),
    })
  }

  if (isSra) {
    details.push({
      label: 'SRA #',
      value: directory.sra?.number,
    })
  }

  if (isFca) {
    details.push({
      label: 'Status',
      value: (
        <>
          {isFcaTradingName ? (
            <>
              Trading name of{' '}
              <Link
                className="text-dim"
                to={ROUTES.companyProfile({
                  searchParams: {
                    identifier: directory.fca?.frn ?? '',
                    identifierType: COMPANY_PROFILE_IDENTIFIER_TYPE.frn,
                    backLabel: `Back to ${directory.name}`,
                    backUrl: currentUrl(),
                  },
                })}
              >
                {directory.fca?.name}
              </Link>
            </>
          ) : (
            directory.fca?.status
          )}
        </>
      ),
    })

    details.push({
      label: 'FCA #',
      value: (
        <ExternalLink
          href={`https://register.fca.org.uk/s/firm?id=${directory.fca?.fcaId}`}
        >
          {directory.fca?.frn}
        </ExternalLink>
      ),
    })
  }

  if (companyProfile.isRegistered && companyDetail?.ifa) {
    if (typeof companyDetail?.ifa.isTransactionalWork === 'boolean') {
      details.push({
        label: 'Does one-off, transactional work',
        value: bool2Human(companyDetail?.ifa.isTransactionalWork),
      })
    }

    if (isRegisteredAsIfa) {
      details.push({
        label: 'Minimum investable assets',
        value: (
          <>
            {companyDetail?.ifa.minInvestableAssets
              ? companyDetail.ifa.minInvestableAssets.toLocaleString('en-GB', {
                  style: 'currency',
                  currency: 'GBP',
                  minimumFractionDigits: 0,
                })
              : 'No'}
          </>
        ),
      })
    }
  }

  if (!isFcaTradingName && directory && directory.postcode) {
    details.push({
      label: 'HQ',
      value:
        directory.address + ' ' + directory.county + ' ' + directory.postcode,
    })
  }

  if (
    (isSra && directory.tradingNames.length > 0) ||
    (isFca && !isFcaTradingName && directory.tradingNames.length > 1)
  ) {
    details.push({
      label: 'Trading Names',
      value: (
        <CollapsibleList
          collapseThreshold={5}
          items={directory.tradingNames.map((tradingName) => tradingName.name)}
        />
      ),
    })
  }

  return (
    <>
      <SectionHeading>At a glance</SectionHeading>

      <Table className="sm:mt- mt-4 text-xs">
        <TableBody>
          {details.map((detail, index) => (
            <TableRow key={index}>
              <TableCell className="min-w-32.5 whitespace-normal">
                <span className="font-medium text-black">{detail.label}</span>
              </TableCell>

              <TableCell className="whitespace-normal">
                {typeof detail.value === 'function'
                  ? detail.value()
                  : detail.value}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </>
  )
}

export default AtAGlance
